import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, FolderPlus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import HelpDot from '@/components/ui/HelpDot';
import Spinner from '@/components/ui/Spinner';
import WhyBox from '@/components/ui/WhyBox';
import { apiFetch } from '@/lib/api';
import { cn } from '@/lib/utils';

const FIELD_CLS =
  'w-full rounded-md border border-border bg-elevated px-3 py-2.5 text-[13.5px] outline-none focus:border-primary';

function RequiredTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full border border-destructive/30 bg-destructive/10 px-2 py-0.5 font-mono text-[10px] text-destructive">
      {children}
    </span>
  );
}

/**
 * New Project — a deliberately small first step.
 *
 * A project starts as a named container; the script and the creative brief are
 * uploaded on the project page, exactly like every later revision. Keeping the
 * script out of this form is what makes Writers Room the single place scripts
 * enter the system.
 */
export default function ScriptInput() {
  const navigate = useNavigate();
  const { t } = useTranslation(['setup', 'common']);

  const [title, setTitle] = useState('');
  const [productionType, setProductionType] = useState('generative');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [iconikCollectionId, setIconikCollectionId] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isFootage = productionType === 'footage';

  const create = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const resp = await apiFetch('/api/projects', {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(),
          aspect_ratio: aspectRatio,
          production_type: productionType,
          iconik_collection_id: iconikCollectionId.trim(),
        }),
      });
      if (!resp.ok) throw new Error(`Server error: ${resp.status}`);
      const project = await resp.json();
      navigate(`/projects/${project.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error'));
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl px-8 py-8">
      <button
        onClick={() => navigate('/projects')}
        className="mb-5 inline-flex items-center gap-1.5 text-[13px] text-faint transition-colors hover:text-primary"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        {t('project.allProjects', { ns: 'dashboard' })}
      </button>

      <div className="rounded-xl border border-border bg-card p-7">
        <h2 className="m-0 font-display text-[19px] font-semibold">{t('title')}</h2>
        <p className="mb-5 mt-1.5 text-[12.5px] leading-relaxed text-faint">{t('subtitle')}</p>

        <WhyBox title={t('why')} className="mb-6">
          {t('whyBody')}
        </WhyBox>

        <div className="space-y-5">
          <label className="block">
            <span className="mb-2 flex items-center gap-2">
              <span className="text-[13.5px] font-medium">{t('field.title')}</span>
              <RequiredTag>{t('field.required')}</RequiredTag>
            </span>
            <input
              autoFocus
              type="text"
              className={FIELD_CLS}
              placeholder={t('field.titlePlaceholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && create()}
            />
          </label>

          <label className="block">
            <span className="mb-2 flex items-center gap-2">
              <span className="text-[13.5px] font-medium">{t('field.type')}</span>
              <RequiredTag>{t('field.required')}</RequiredTag>
            </span>
            <select className={FIELD_CLS} value={productionType} onChange={(e) => setProductionType(e.target.value)}>
              <option value="generative">{t('field.typeGenerative')}</option>
              <option value="footage">{t('field.typeFootage')}</option>
            </select>
          </label>

          <div>
            <button
              type="button"
              onClick={() => setAdvanced((a) => !a)}
              className="flex w-full items-center gap-2 border-t border-dashed border-border pt-3
                         text-[12.5px] text-muted-foreground transition-colors hover:text-foreground"
            >
              <ChevronRight className={cn('h-3.5 w-3.5 text-primary transition-transform', advanced && 'rotate-90')} />
              {t('advanced')}
            </button>

            {advanced && (
              <div className="space-y-5 pt-4">
                <label className="block">
                  <span className="mb-2 flex items-center gap-1.5 text-[13.5px] font-medium">
                    {t('field.aspect')}
                    <HelpDot>{t('field.aspectHelp')}</HelpDot>
                  </span>
                  <select className={FIELD_CLS} value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)}>
                    <option value="16:9">{t('launch.aspect169', { ns: 'dashboard' })}</option>
                    <option value="9:16">{t('launch.aspect916', { ns: 'dashboard' })}</option>
                  </select>
                </label>

                {isFootage && (
                  <label className="block">
                    <span className="mb-2 flex items-center gap-1.5 text-[13.5px] font-medium">
                      {t('field.collection')}
                      <HelpDot>{t('field.collectionHelp')}</HelpDot>
                    </span>
                    <input
                      type="text"
                      className={`${FIELD_CLS} font-mono`}
                      placeholder={t('field.collectionPlaceholder')}
                      value={iconikCollectionId}
                      onChange={(e) => setIconikCollectionId(e.target.value)}
                    />
                  </label>
                )}
              </div>
            )}
          </div>
        </div>

        {error && <p className="mt-4 text-[12.5px] text-destructive">{error}</p>}

        <div className="mt-7 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/projects')}>
            {t('action.cancel', { ns: 'common' })}
          </Button>
          <Button onClick={create} disabled={submitting || !title.trim()}>
            {submitting ? <Spinner className="mr-2" /> : <FolderPlus className="mr-2 h-4 w-4" />}
            {submitting ? t('creating') : t('create')}
          </Button>
        </div>
      </div>
    </div>
  );
}
